import { Card, CbhIcon, Text } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { CardContent, Stack, ThemeProvider } from "@mui/material";
import { useUpdateAddress } from "@src/appV2/Address/api/useUpdateAddress";
import { AddressInputBottomSheet } from "@src/appV2/Address/components/AddressInputBottomSheet";
import { ADDRESS_ERROR_MESSAGES } from "@src/appV2/Address/constants";
import { useWorkerAddress } from "@src/appV2/Address/hooks/useWorkerAddress";
import { getFormattedAddressLines } from "@src/appV2/Address/utils/getFormattedAddressLines";
import { isSupportedCountryCode, isValidPlace } from "@src/appV2/Address/utils/validation";
import { useToast } from "@src/appV2/lib";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "@src/appV2/lib/analytics";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { PageFooterWrapper } from "@src/appV2/redesign/components/PageFooterWrapper";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";
import { useGetWorker } from "@src/appV2/Worker/api/useGetWorker";
import { useRef } from "react";

import { TabRouterPath } from "../../../app/routing/constant/tabRoute";

export function ProfileAddressPage() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { showErrorToast } = useToast();

  const {
    place,
    addressInputModalState,
    addressInput,
    handleAddressInputChange,
    handlePlaceChange,
    handleManualEntry,
    addressInputErrorMessage,
  } = useWorkerAddress({
    shouldShowEditAddress: false,
    initializeFromWorker: false,
  });

  const { data: worker } = useGetWorker();

  const { mutate: updateAddressMutation, isLoading: isUpdatingProfileAddress } = useUpdateAddress({
    onSuccess: () => {
      logEvent(APP_V2_USER_EVENTS.UPDATED_ADDRESS);
      addressInputModalState.closeModal();
    },
    onError: () => {
      showErrorToast(ADDRESS_ERROR_MESSAGES.UNEXPECTED_ERROR);
    },
  });

  function handleSave() {
    if (!isValidPlace(place)) {
      showErrorToast(ADDRESS_ERROR_MESSAGES.INVALID_ADDRESS);
      return;
    }

    if (!isSupportedCountryCode(place.address.countryCode)) {
      showErrorToast(ADDRESS_ERROR_MESSAGES.US_ONLY);

      logError(APP_V2_APP_EVENTS.UPDATE_ADDRESS_ERROR, {
        error: new Error(ADDRESS_ERROR_MESSAGES.US_ONLY),
        metadata: {
          workerId: worker?.userId,
        },
      });
      return;
    }

    updateAddressMutation(place);
  }

  const addressLines = getFormattedAddressLines(worker?.address);

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <StickyScrollPageHeader
          title="Home Address"
          scrollContainerRef={scrollRef}
          fallbackGoBackPath={TabRouterPath.PROFILE}
          headerVariant="tertiary"
        />
        <PageContent ref={scrollRef}>
          <Stack spacing={10}>
            <Text color={(theme) => theme.palette.text.secondary}>
              Set your home address to see nearby shifts
            </Text>
            <Card outlined variant="tertiary">
              <CardContent>
                <Stack direction="row" spacing={2}>
                  <CbhIcon type="map-pin" />
                  <Stack spacing={2}>
                    <Text semibold variant="h6" color={(theme) => theme.palette.text.primary}>
                      Current home address
                    </Text>
                    <Stack spacing={1}>
                      {addressLines.map((line) => (
                        <Text
                          key={line}
                          variant="body1"
                          color={(theme) => theme.palette.text.primary}
                        >
                          {line}
                        </Text>
                      ))}
                    </Stack>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </PageContent>

        <PageFooterWrapper>
          <LoadingButton
            fullWidth
            size="large"
            variant="contained"
            isLoading={isUpdatingProfileAddress}
            onClick={() => {
              // Open modal with empty address state
              handleManualEntry({ resetAddress: true });
            }}
          >
            Update address
          </LoadingButton>
        </PageFooterWrapper>

        <AddressInputBottomSheet
          modalState={addressInputModalState}
          place={place}
          isContinueLoading={isUpdatingProfileAddress}
          isContinueDisabled={!isValidPlace(place) || Boolean(addressInputErrorMessage)}
          addressInput={addressInput || ""}
          addressInputErrorMessage={addressInputErrorMessage}
          title="Update home address"
          buttonText="Save"
          onPlaceChanged={(newPlace) => {
            handlePlaceChange(newPlace);
          }}
          onContinue={() => {
            handleSave();
          }}
          onAddressInputChange={handleAddressInputChange}
        />
      </PageWrapper>
    </ThemeProvider>
  );
}
